import { useState } from "react";

import { Box, Heading, Stack } from "@chakra-ui/react";
import { createColumnHelper, SortingState } from "@tanstack/react-table";

import {
  getExternalProvidersErrorsExternalProvidersErrorsGet,
  ExternalProviderErrorType,
} from "@/codegen/openapi2";
import { TableLayout } from "@/components/2/layouts/table";
import { useGetExternalProvidersErrorsExternalProvidersErrorsGet } from "@/services/2/react-query/hooks";

import { ThirdPartiesErrorsFilters } from "./filters";
import { ThirdPartiesErrorsFiltersType } from "./types";

const columnHelper = createColumnHelper<ExternalProviderErrorType>();

const columns = [
  columnHelper.accessor("provider", {
    header: "Fournisseur",
    cell: ({ cell }) => {
      const provider = cell.getValue();
      const providerMap: Record<string, string> = {
        google: "Google",
        microsoft: "Microsoft",
        slack: "Slack",
      };
      return providerMap[provider] || provider;
    },
    minSize: 150,
  }),
  columnHelper.accessor("error_type", {
    header: "Type d'erreur",
    cell: ({ cell }) => {
      const errorType = cell.getValue();
      const errorTypeMap: Record<string, string> = {
        gone_but_active: "Parti mais toujours actif",
        long_gone_but_still_archived: "Parti depuis longtemps mais archivé",
        no_link: "Pas de lien",
      };
      return errorTypeMap[errorType] || errorType;
    },
    minSize: 300,
  }),
  columnHelper.accessor("mailish", {
    header: "Email",
    cell: ({ cell }) => cell.getValue() || "-",
  }),
  columnHelper.accessor("name", {
    header: "Nom",
    cell: ({ cell }) => cell.getValue() || "-",
    minSize: 400,
  }),
  columnHelper.accessor("last_day_date", {
    header: "Dernier jour",
    cell: ({ cell }) => cell.getValue() || "-",
    minSize: 120,
  }),
];

export const ThirdPartiesErrors = () => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });

  const [sorting, setRawSorting] = useState<SortingState>([]);
  const [filters, setRawFilters] = useState<ThirdPartiesErrorsFiltersType>({});

  // Reset to first page when sorting or filters change
  const setSorting = (updater: SortingState | ((old: SortingState) => SortingState)) => {
    setPagination(prev => ({
      ...prev,
      pageIndex: 0,
    }));
    setRawSorting(updater);
  };
  const setFilters = (
    updater:
      | ThirdPartiesErrorsFiltersType
      | ((old: ThirdPartiesErrorsFiltersType) => ThirdPartiesErrorsFiltersType)
  ) => {
    setPagination(prev => ({
      ...prev,
      pageIndex: 0,
    }));
    setRawFilters(updater);
  };

  const csvMetadata = {
    filename: "erreurs-fournisseurs-tiers.csv",
    columns: [
      { header: "ID", getter: (row: ExternalProviderErrorType) => row.id },
      { header: "Provider", getter: (row: ExternalProviderErrorType) => row.provider },
      { header: "Error Type", getter: (row: ExternalProviderErrorType) => row.error_type },
      { header: "Email", getter: (row: ExternalProviderErrorType) => row.mailish || "" },
      { header: "Name", getter: (row: ExternalProviderErrorType) => row.name || "" },
      {
        header: "Last Day Date",
        getter: (row: ExternalProviderErrorType) => row.last_day_date || "",
      },
      { header: "Created At", getter: (row: ExternalProviderErrorType) => row.created_at || "" },
    ],
    fetchAllData: async () => {
      const response = await getExternalProvidersErrorsExternalProvidersErrorsGet({
        query: {
          size: 0, // Unpaginated - fetch all data
          sort: sorting[0] ? `${sorting[0].desc ? "-" : "+"}${sorting[0].id}` : undefined,
          providers: filters.providers?.length ? filters.providers : undefined,
          error_types: filters.errorTypes?.length ? filters.errorTypes : undefined,
        },
      });
      return response.data?.resources || [];
    },
  };

  const { data, isLoading } = useGetExternalProvidersErrorsExternalProvidersErrorsGet(
    {
      query: {
        page: pagination.pageIndex + 1, // Convert from 0-based to 1-based
        size: pagination.pageSize,
        sort: sorting[0] ? `${sorting[0].desc ? "-" : "+"}${sorting[0].id}` : undefined,
        providers: filters.providers?.length ? filters.providers : undefined,
        error_types: filters.errorTypes?.length ? filters.errorTypes : undefined,
      },
    },
    ["external-providers-errors", pagination, sorting, filters],
    {
      placeholderData: { resources: [], pagination: { page: 1, page_size: 50, item_count: 0 } },
    }
  );

  return (
    <Stack>
      <Box p={8} backgroundColor={"gray.100"}>
        <Heading size="2xl">Erreurs fournisseurs tiers</Heading>
      </Box>

      <Stack p={8} gap={4}>
        <ThirdPartiesErrorsFilters filters={filters} setFilters={setFilters} />
        {!isLoading && (
          <TableLayout
            data={data?.resources || []}
            columns={columns}
            withManualPagination
            rowCount={data?.pagination.item_count || 0}
            pagination={pagination}
            setPagination={setPagination}
            withSorting={true}
            sorting={sorting}
            setSorting={setSorting}
            csvMetadata={csvMetadata}
          />
        )}
      </Stack>
    </Stack>
  );
};
